import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Switch } from '@/components/ui/Switch'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/Select'
import { ImageUploadField } from '@/components/help_center/ImageUploadField'
import { FooterLinksEditor } from '@/components/help_center/FooterLinksEditor'
import { NavLinksEditor } from '@/components/help_center/NavLinksEditor'
import { TemplatePicker } from '@/components/help_center/TemplatePicker'
import { PaletteStudio } from '@/components/help_center/PaletteStudio'
import { useBooleanEntitlement } from '@/hooks/useEntitlement'
import type { HelpCenterSettings, HelpCenterTheme, HelpCenterBranding } from '@/pages/HelpCenter/types'

interface ThemeEditorProps {
  settings: HelpCenterSettings
  theme: HelpCenterTheme
  branding: HelpCenterBranding
  // Product brand colours — seed the palette generator + swatch fallbacks.
  brandPrimary?: string | null
  brandAccent?: string | null
  onThemeChange: (theme: HelpCenterTheme) => void
  onBrandingChange: (branding: HelpCenterBranding) => void
}

// Keep in sync with HelpCenterTheme::FONTS (app/presenters/help_center_theme.rb).
const FONT_OPTIONS: { value: string; label: string }[] = [
  { value: 'sans', label: 'System sans' },
  { value: 'serif', label: 'System serif' },
  { value: 'mono', label: 'System mono' },
  { value: 'inter', label: 'Inter' },
  { value: 'roboto', label: 'Roboto' },
  { value: 'open-sans', label: 'Open Sans' },
  { value: 'lato', label: 'Lato' },
  { value: 'montserrat', label: 'Montserrat' },
  { value: 'poppins', label: 'Poppins' },
  { value: 'nunito', label: 'Nunito' },
  { value: 'work-sans', label: 'Work Sans' },
  { value: 'source-sans-3', label: 'Source Sans 3' },
  { value: 'merriweather', label: 'Merriweather' },
  { value: 'playfair', label: 'Playfair Display' },
  { value: 'lora', label: 'Lora' },
  { value: 'jetbrains-mono', label: 'JetBrains Mono' },
]

export function ThemeEditor({ settings, theme, branding, brandPrimary, brandAccent, onThemeChange, onBrandingChange }: ThemeEditorProps) {
  const setTheme = (patch: Partial<HelpCenterTheme>) => onThemeChange({ ...theme, ...patch })
  const canRemoveBranding = useBooleanEntitlement('help_center_remove_branding').enabled
  const previewPrimary = theme.primary_color || brandPrimary || '#10b981'
  const previewAccent = theme.accent_color || brandAccent || previewPrimary

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Layout</CardTitle>
          <CardDescription>Choose a home-page template — restyle any of them with the colours and fonts below.</CardDescription>
        </CardHeader>
        <CardContent>
          <TemplatePicker
            value={theme.template ?? 'classic'}
            primary={previewPrimary}
            accent={previewAccent}
            onChange={v => setTheme({ template: v })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Theme</CardTitle>
          <CardDescription>Brand your published help center.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Appearance</Label>
              <Select value={theme.appearance ?? 'dark'} onValueChange={v => setTheme({ appearance: v as HelpCenterTheme['appearance'] })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="system">Match system</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Header style</Label>
              <Select value={theme.header_style ?? 'neutral'} onValueChange={v => setTheme({ header_style: v as HelpCenterTheme['header_style'] })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="neutral">Neutral</SelectItem>
                  <SelectItem value="branded">Branded band</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Font</Label>
              <Select value={theme.font ?? 'sans'} onValueChange={v => setTheme({ font: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FONT_OPTIONS.map(f => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Corners</Label>
              <Select value={theme.radius ?? 'default'} onValueChange={v => setTheme({ radius: v as HelpCenterTheme['radius'] })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sharp">Sharp</SelectItem>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="rounded">Rounded</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Hero text over image</Label>
              <Select value={theme.hero_text ?? 'light'} onValueChange={v => setTheme({ hero_text: v as HelpCenterTheme['hero_text'] })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Light text</SelectItem>
                  <SelectItem value="dark">Dark text</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Applies when a hero background image is set.</p>
            </div>
            {theme.template === 'aurora' && (
              <div className="space-y-2">
                <Label>Hero background</Label>
                <Select value={theme.hero_gradient ?? 'mesh'} onValueChange={v => setTheme({ hero_gradient: v as HelpCenterTheme['hero_gradient'] })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mesh">Mesh gradient (vibrant)</SelectItem>
                    <SelectItem value="soft">Soft glow</SelectItem>
                    <SelectItem value="solid">Solid</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Only the Aurora layout uses a hero gradient.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Colours</CardTitle>
          <CardDescription>
            Inherited from <span className="font-medium text-foreground">Product &amp; Brand</span>. Generate a matching
            palette or override any layer for this help center.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PaletteStudio theme={theme} brandPrimary={brandPrimary} brandAccent={brandAccent} onThemeChange={setTheme} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Brand assets</CardTitle>
          <CardDescription>
            Logo, favicon and images. PNG or SVG recommended. Images save immediately — other settings save with "Save changes".
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <ImageUploadField label="Logo" field="logo" currentUrl={settings.logo_url} hint="Header · PNG/SVG · ~240×60 (4:1)" />
          <ImageUploadField label="Favicon" field="favicon" currentUrl={settings.favicon_url} hint="Browser tab · square · 32×32 or 64×64" />
          <ImageUploadField label="Hero background" field="hero_image" currentUrl={settings.hero_image_url} hint="Behind the search · ~1600×600 (8:3) · JPG/WebP" />
          <ImageUploadField label="Social share image" field="og_image" currentUrl={settings.og_image_url} hint="Open Graph · 1200×630 (1.91:1)" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Navigation &amp; footer</CardTitle>
          <CardDescription>Links in the top navigation and the footer columns.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Navigation links</Label>
            <p className="text-xs text-muted-foreground">Shown in the header. Use full URLs for external pages or /paths inside the help center.</p>
            <NavLinksEditor
              links={branding.nav_links ?? []}
              onChange={navLinks => onBrandingChange({ ...branding, nav_links: navLinks })}
            />
          </div>

          <div className="space-y-2 border-t border-border pt-4">
            <Label>Footer text</Label>
            <Input
              value={branding.footer_text ?? ''}
              onChange={e => onBrandingChange({ ...branding, footer_text: e.target.value })}
              placeholder="© Your Company"
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label>Show "Powered by Banshee"</Label>
              {!canRemoveBranding && (
                <p className="text-xs text-muted-foreground">Upgrade to a paid plan to remove Banshee branding.</p>
              )}
            </div>
            <Switch
              checked={canRemoveBranding ? (branding.show_powered_by ?? true) : true}
              disabled={!canRemoveBranding}
              onCheckedChange={v => onBrandingChange({ ...branding, show_powered_by: v })}
            />
          </div>

          <div className="space-y-2 border-t border-border pt-4">
            <Label>Footer link columns</Label>
            <p className="text-xs text-muted-foreground">Link back to your product, docs, socials, etc.</p>
            <FooterLinksEditor
              columns={branding.footer_columns ?? []}
              onChange={cols => onBrandingChange({ ...branding, footer_columns: cols })}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
